import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Get, HTTPError, Post, Put } from "@repo/utils/api";
import UObject from "@repo/utils/UObject";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Customer from "@repo/vygor-models/Customer";

import { api } from "..";

import { Bookable, useBookables } from "./bookables";
import { Booking, useBookings } from "./bookings";
import { Membership, useMemberships } from "./memberships";
import { Organization, useOrganizations } from "./organizations";
import { PaymentMethod, usePaymentMethods } from "./paymentMethods";
import FullName from "@repo/utils/FullName";
import Chainable from "@repo/utils/Chainable";
import StorePair from "@repo/utils-solid/StorePair";
import UPromise from "@repo/utils/UPromise";

export { Customer };

export type CustomersRest = Get<
  {},
  {
    customers: Customer[];
    memberships: Membership[];
    membershipPlans: Membership.Plan[];
    organizations: Organization.FromApi[];
  }
> & {
  [id: Customer.Id]: Get<
    {},
    {
      customer: Customer;
      memberships: Membership[];
      membershipPlans: Membership.Plan[];
    }
  > & {
    "bookings": Get<{}, { bookings: Booking[]; bookables: Bookable[] }>;
    "memberships": Get<
      {},
      { memberships: Membership[]; membershipPlans: Membership.Plan[] }
    >;
    "payment-methods": Get<{}, { paymentMethods: PaymentMethod[] }>;
  };
};

export namespace CustomersRest {
  export type Organization = Get<
    {
      searchQuery?: string;
      page?: number;
    },
    { customers: Customer[] }
  > &
    Post<
      {
        email: string;
        name?: FullName;
        phone?: string;
      },
      {
        customer: Customer;
        userExists: boolean;
      }
    > & {
      [id in Customer.Id]: Get<
        {},
        {
          customer: Customer;
          memberships: Membership[];
          membershipPlans: Membership.Plan[];
        }
      >;
    } & {
      self: Get<
        { createIfMissing?: boolean },
        {
          customer: Customer;
          memberships: Membership[];
          membershipPlans: Membership.Plan[];
        }
      > &
        Put<{}, { customer: Customer }>;
    };
}

export type CustomerGroupsRest = {};
export namespace CustomerGroupsRest {
  export type Organization = Get<
    {},
    { customerGroups: Customer.Group.Query[] }
  >;
}

const initialState = {
  byId: {} as Record<Customer.Id, Customer>,
  membershipsByCustomerId: {} as Record<Customer.Id, Membership.Id[]>,
  bookingsByCustomerId: {} as Record<Customer.Id, Booking.Id[]>,
  paymentMethodsById: {} as Record<Customer.Id, PaymentMethod.Id[]>,
  ownCustomers: null as null | Customer.Id[],
  groups: {
    byId: {} as Record<Customer.Group.Id, Customer.Group>,
    queryById: {} as Record<Customer.Group.Query.Id, Customer.Group.Query>,
  },
  forOrganization: {
    byId: {} as Record<
      Organization.Id,
      { byQuery: Record<string, Customer.Id[] & { page: number }> }
    >,
    own: {} as Record<Organization.Id, Customer.Id>,
    groupQueries: {} as Record<Organization.Id, Customer.Group.Query.Id[]>,
  },
};

export const { Context, Provider, useCustomers } = FeatureStore.init(
  "Customers",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const [bookings, setBookings] = useBookings();
    const [bookables, setBookables] = useBookables();
    const [memberships, setMemberships] = useMemberships();
    const [paymentMethods, setPaymentMethods] = usePaymentMethods();
    const [, setOrganizations] = useOrganizations();

    const accessors = {
      byId: attachAction(
        (id: Customer.Id | undefined) => id && store.byId[id],
        (id) => id && actions.fetchSingle(id),
        (id) => (res) => !id || res,
      ),
      own: attachAction(
        () => store.ownCustomers,
        () => actions.fetchOwn(),
      ),
      membershipsFor: attachAction(
        (id: Customer.Id) =>
          store.membershipsByCustomerId[id]?.map((id) => memberships.byId(id)!),
        (id) => actions.fetchMemberships(id),
      ),
      bookingsFor: Object.assign(
        attachAction(
          (id: Customer.Id) =>
            store.bookingsByCustomerId[id]?.map((id) => bookings.byId(id)!),
          (id) => actions.fetchBookings(id),
        ),
        {
          upcoming: (id: Customer.Id) =>
            accessors
              .bookingsFor(id)
              ?.filter(
                ({ bookableId }) =>
                  (bookables.byId(bookableId)?.startAt.getTime() || 0) >
                  Date.now(),
              ),
        },
      ),
      paymentMethodsFor: attachAction(
        (id: Customer.Id | undefined) =>
          id &&
          store.paymentMethodsById[id]?.map((id) => paymentMethods.byId(id)!),
        (id) => id && actions.fetchPaymentMethods(id),
      ),
      groups: {
        byId: (id: Customer.Group.Id) => store.groups.byId[id],
        queryById: (id: Customer.Group.Query.Id) => store.groups.queryById[id],
      },
      forOrganization: Object.assign(
        attachAction(
          (id: Organization.Id, search = "") =>
            store.forOrganization.byId[id]?.byQuery[search]?.map(
              (id) => accessors.byId(id)!,
            ),
          (id, search = "") => actions.forOrganization.fetch(id, search),
        ),
        {
          own: attachAction(
            (orgId: Organization.Id | undefined) =>
              orgId && store.byId[store.forOrganization.own[orgId]!],
            (orgId) => orgId && actions.forOrganization.fetchOwn(orgId),
          ),
          groupQueries: attachAction(
            (orgId: Organization.Id) =>
              store.forOrganization.groupQueries[orgId]?.map(
                (id) => store.groups.queryById[id]!,
              ),
            (orgId) => actions.forOrganization.fetchGroups(orgId),
          ),
        },
      ),
    };

    const mutations = {
      one(customer: Customer) {
        setStore("byId", customer.id, reconcile(customer));
      },
      many(customers: Customer[]) {
        batch(() => {
          for (const customer of customers) {
            mutations.one(customer);
          }
        });
      },
      groups: {
        one(group: Customer.Group) {
          setStore("groups", "byId", group.id, reconcile(group));
        },
        many(groups: Customer.Group[]) {
          for (const group of groups) {
            mutations.groups.one(group);
          }
        },
        oneQuery(query: Customer.Group.Query) {
          setStore("groups", "queryById", query.id, reconcile(query));
        },
        manyQueries(queries: Customer.Group.Query[]) {
          for (const query of queries) {
            mutations.groups.oneQuery(query);
          }
        },
      },
      removeMembership(membership: Membership) {
        setStore("membershipsByCustomerId", membership.customerId, (prev) =>
          prev?.filter((t) => t !== membership.id),
        );
      },
    };

    const actions = wrapActions({
      one: mutations.one,
      many: mutations.many,
      groups: mutations.groups,
      removeMembership: mutations.removeMembership,
      fetchSingle(id: Customer.Id) {
        return api.customers[id]!.GET({}).then(
          ({ customer, memberships, membershipPlans }) => {
            batch(() => {
              mutations.one(customer);
              setMemberships.many(memberships);
              setMemberships.manyPlans(membershipPlans);
            });
            return customer;
          },
        );
      },
      fetchBookings(id: Customer.Id) {
        return api.customers[id]!.bookings.GET({}).then(
          ({ bookings, bookables }) => {
            batch(() => {
              setBookables.many(bookables);
              setBookings.many(bookings);
              setStore(
                "bookingsByCustomerId",
                id,
                bookings.map(({ id }) => id),
              );
            });
          },
        );
      },
      fetchMemberships(id: Customer.Id) {
        return api.customers[id]!.memberships.GET({}).then(
          ({ memberships, membershipPlans }) => {
            batch(() => {
              setMemberships.manyPlans(membershipPlans);
              setMemberships.many(memberships);
              setStore(
                "membershipsByCustomerId",
                id,
                memberships.map(({ id }) => id),
              );
            });
          },
        );
      },
      fetchPaymentMethods(id: Customer.Id) {
        return api.customers[id]!["payment-methods"].GET({}).then(
          ({ paymentMethods }) => {
            batch(() => {
              setStore(
                "paymentMethodsById",
                id,
                paymentMethods.map(({ id }) => id),
              );
              setPaymentMethods.many(paymentMethods);
            });
          },
        );
      },
      fetchOwn() {
        return api.customers
          .GET({})
          .then(
            ({ customers, memberships, membershipPlans, organizations }) => {
              batch(() => {
                mutations.many(customers);
                setMemberships.many(memberships);
                setMemberships.manyPlans(membershipPlans);
                setOrganizations.many(
                  organizations.map(Organization.normalize),
                );

                setStore(
                  "forOrganization",
                  "own",
                  Object.fromEntries(
                    customers.map(({ id, organizationId }) => [
                      organizationId,
                      id,
                    ]),
                  ),
                );
                setStore(
                  "ownCustomers",
                  customers.map(({ id }) => id),
                );
                setStore(
                  "membershipsByCustomerId",
                  UObject.groupByEntry(memberships, ({ customerId, id }) => [
                    customerId,
                    id,
                  ]),
                );
              });
            },
          );
      },
      forOrganization: {
        fetch: Chainable.create(
          Chainable.wrapFactory(
            ({ more = false }) =>
              async (orgId: Organization.Id, search = "") => {
                const page =
                  !more || !store.forOrganization.byId[orgId]?.byQuery[search]
                    ? 0
                    : store.forOrganization.byId[orgId].byQuery[search].page +
                      1;

                return api.organizations[orgId]!.customers.GET({
                  page,
                  searchQuery: search,
                }).then(({ customers }) => {
                  batch(() => {
                    mutations.many(customers);
                    StorePair.safeSetter([store, setStore])(
                      "forOrganization",
                      "byId",
                      orgId,
                      "byQuery",
                      search,
                      (prev) =>
                        Object.assign(
                          [...(prev || []), ...customers.map(({ id }) => id)],
                          { page },
                        ),
                    );
                  });
                  return customers;
                });
              },
          ),
          { more: { more: true } },
        ),
        create(
          orgId: Organization.Id,
          params: UObject.PartialBy<Customer.Params, "name">,
        ) {
          return api.organizations[orgId]!.customers.POST(params).then(
            ({ customer, userExists }) => {
              batch(() => {
                mutations.one(customer);
                StorePair.safeSetter([store, setStore])(
                  "forOrganization",
                  "byId",
                  orgId,
                  "byQuery",
                  "",
                  (customers = Object.assign([], { page: 0 })) =>
                    Object.assign([customer.id, ...customers], {
                      page: customers.page,
                    }),
                );
              });
              if (userExists) {
                throw Object.assign(new Error("User already exists"), {
                  userExists,
                });
              }
              return customer;
            },
          );
        },
        fetchOwn: Chainable.create(
          Chainable.wrapFactory(
            ({ createIfMissing = false }) =>
              (orgId: Organization.Id) => {
                return api.organizations[orgId]!.customers.self.GET({
                  createIfMissing,
                })
                  .then(({ customer, memberships, membershipPlans }) => {
                    batch(() => {
                      mutations.one(customer);
                      setStore("forOrganization", "own", orgId, customer.id);
                      setMemberships.many(memberships);
                      setMemberships.manyPlans(membershipPlans);
                    });
                    return { customer, memberships, membershipPlans };
                  })
                  .catch((e) => {
                    if (e instanceof HTTPError && e.response.status === 404)
                      return;
                    throw e;
                  });
              },
          ),
          { createIfMissing: { createIfMissing: true } },
        ),
        fetchGroups(orgId: Organization.Id) {
          return api.organizations[orgId]!["customer-groups"].GET({}).then(
            ({ customerGroups }) => {
              batch(() => {
                mutations.groups.manyQueries(customerGroups);
                setStore(
                  "forOrganization",
                  "groupQueries",
                  orgId,
                  customerGroups.map(({ id }) => id),
                );
              });
            },
          );
        },
      },
    });

    return { accessors, actions };
  },
);
